import { faChevronsRight, faLock, faPaperPlane, faPen } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { isNil } from "lodash";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Loader, Segment } from "semantic-ui-react";
import { markdownToRichTextEditorDocumentJSON } from "../../../../../../../components/global_components/form_components/controlled-rich-text-editor/util/controlledRichTextEditorMarkdown";
import { NotifyTopNav } from "../../../../../components/NotifyTopNav";
import { useNotifyContentDetails } from "../../../../../hooks/useNotifyContent";
import { BreadcrumbItem } from "../../components/BreadcrumbItem";
import {
  NotifyNotificationsForm,
  NotifyNotificationsFormData,
  NotifyNotificationsFormDefaults,
  notifyNotificationsFormResolver,
} from "../../components/NotifyNotificationsForm";
import { NotifyNotificationsPreview } from "../../components/NotifyNotificationsPreview";
import { NotifyNotificationsReview } from "../../components/NotifyNotificationsReview";
import { useContentHeight } from "../../hooks/useContentHeight";
import { NotifyNotificationsTemplates } from "../create-from-template/constants/NotifyNotificationsTemplates";
import {
  NotifyNotificationsStatusLabel,
  NotifyNotificationsStatusLabelType,
} from "../notifications/components/NotifyNotificationsStatusLabel";
import {
  NotifyNotificationsActionMode,
  useNotifyNotificationsActions,
} from "../notifications/hooks/useNotifyNotificationsActions";
import { NotifyNotificationEstimatedReach } from "../../components/NotifyNotificationEstimatedReach";

export function NotifyNotificationsCreatePage() {
  const [step, setStep] = useQueryState(
    "step",
    parseAsStringEnum(["edit", "review"]).withDefault("edit").withOptions({
      clearOnDefault: false,
      history: "push",
    }),
  );

  const {
    addMediaFile,
    onContinue,
    onSaveDraft,
    isSaveDraftLoading,
    onSend,
    isSendLoading,
    onSchedule,
    isScheduleLoading,
    convertContentToFormData,
  } = useNotifyNotificationsActions({ mode: NotifyNotificationsActionMode.Create, setStep });

  const form = useForm<NotifyNotificationsFormData>({
    defaultValues: NotifyNotificationsFormDefaults,
    resolver: notifyNotificationsFormResolver,
  });
  const { watch, reset } = form;

  const searchParams = new URLSearchParams(window.location.search);
  const templateType = searchParams.get("template-type") as "template" | "notification" | null;
  const templateId = searchParams.get("template-id");
  const parsedTemplateId = templateId ? parseInt(templateId) : null;

  const topBarRef = useRef<HTMLDivElement>(null);
  const topBarHeight = useContentHeight(topBarRef, {
    defaultFudge: 24.5,
  });

  const { notifyContent, areContentDetailsLoading } = useNotifyContentDetails({
    enabled: templateType === "notification",
    id: parsedTemplateId,
  });

  // Set the default values with the selected template
  useEffect(() => {
    if (step !== "edit") return;

    const canUseExistingNotificationTemplate = templateType === "notification" && notifyContent;
    const canUseTemplate = templateType === "template" && !isNil(parsedTemplateId);

    if (!canUseExistingNotificationTemplate && !canUseTemplate) return;

    if (canUseExistingNotificationTemplate) {
      reset(convertContentToFormData(notifyContent));
    }

    if (canUseTemplate) {
      const { template } = NotifyNotificationsTemplates[parsedTemplateId!];
      if (template) {
        reset({
          ...NotifyNotificationsFormDefaults,
          ...template,
          body: JSON.stringify(markdownToRichTextEditorDocumentJSON(template.body)),
        });
      }
    }
  }, [templateType, parsedTemplateId, notifyContent, reset]);

  const { headline, fromDepartment } = watch(["headline", "fromDepartment"]);

  if (areContentDetailsLoading && templateType === "notification") {
    return (
      <div>
        <NotifyTopNav />
        <Loader active />
      </div>
    );
  }

  return (
    <div className='tw-relative'>
      <div ref={topBarRef} className='tw-sticky tw-top-0 tw-z-50'>
        <NotifyTopNav />
        <Segment
          attached='top'
          className='tw-mt-0 tw-flex tw-items-center tw-justify-between tw-rounded-none tw-border-x-0 tw-px-7 tw-py-3 tw-shadow-md'
        >
          <div className='tw-flex tw-items-center tw-gap-3'>
            <h1 className='tw-m-0 tw-text-2xl'>{headline || "My Notification"}</h1>
            <NotifyNotificationsStatusLabel type='Draft' />
          </div>
          {fromDepartment && (
            <div className='tw-flex tw-items-center tw-gap-gg-1'>
              <FontAwesomeIcon icon={faLock} className='tw-text-text-hint' />
              <span className='tw-text-text-secondary'>{fromDepartment.label}</span>
            </div>
          )}
        </Segment>
        <Segment attached='bottom' className='tw-mt-0 tw-rounded-none tw-border-x-0 tw-px-7 tw-py-3 tw-shadow-md'>
          <div className='tw-flex tw-items-center tw-gap-3'>
            <BreadcrumbItem label='Edit' icon={faPen} active={step === "edit"} onClick={() => setStep("edit")} />
            <FontAwesomeIcon icon={faChevronsRight} className='tw-h-3 tw-w-3 tw-text-text-hint' />
            <BreadcrumbItem
              label='Review'
              icon={faPaperPlane}
              disabled={step === "edit"}
              active={step === "review"}
              onClick={() => setStep("review")}
            />
          </div>
        </Segment>
      </div>
      <FormProvider {...form}>
        <div
          className={classNames("tw-relative tw-m-gg-3 tw-mt-7 tw-flex tw-gap-6 sm:tw-m-7", {
            "tw-hidden": step !== "edit",
          })}
        >
          <div className='tw-w-full tw-max-w-full lg:tw-max-w-[380px] xl:tw-max-w-[500px] 2xl:tw-max-w-[700px]'>
            <NotifyNotificationsForm
              onContinue={onContinue}
              onSaveDraft={onSaveDraft}
              isSaveDraftLoading={isSaveDraftLoading}
              addMediaFile={addMediaFile}
            />
          </div>
          <div className='tw-sticky tw-z-40 tw-hidden tw-h-full tw-w-full lg:tw-block' style={{ top: topBarHeight }}>
            <div
              className='tw-flex tw-flex-col tw-gap-gg-3'
              style={{
                height: `calc(100vh - ${topBarHeight}px - 24.5px)`,
              }}
            >
              <NotifyNotificationsPreview height='100%' />
              <NotifyNotificationEstimatedReach />
            </div>
          </div>
        </div>
        <div className={classNames({ "tw-hidden": step !== "review" })}>
          <NotifyNotificationsReview
            contentStatus={NotifyNotificationsStatusLabelType.Draft}
            onReturnToEditStep={() => setStep("edit")}
            onSaveDraft={onSaveDraft}
            isSaveDraftLoading={isSaveDraftLoading}
            onSend={onSend}
            isSendLoading={isSendLoading}
            onSchedule={onSchedule}
            isScheduleLoading={isScheduleLoading}
          />
        </div>
      </FormProvider>
    </div>
  );
}
